/**
 * L'écran de veille : Steve rebondit sur les quatre bords, façon logo de
 * lecteur DVD.
 *
 * Le mouvement est pur et sans DOM — `step` prend un état et un pas de temps, et
 * rend le suivant. C'est ce qui permet de tester le seul moment qui compte
 * vraiment : le rebond dans un coin, où **les deux composantes s'inversent dans
 * la même image**. C'est le graal de l'écran de veille, et c'est exactement le
 * genre de cas qu'on ne voit jamais en regardant l'écran.
 */

/** Vitesse de déplacement, en pixels par seconde (cf. handoff). */
export const SPEED_PX_S = 90;

/** Inactivité avant déclenchement. */
export const IDLE_MS = 40_000;

/**
 * Délai de grâce après l'ouverture, avant qu'un geste puisse refermer l'écran.
 *
 * L'écran s'ouvre depuis une entrée de menu, donc au **clic** — et le mouvement
 * de souris qui suit ce clic est lui-même un événement de réveil. Sans ce délai,
 * l'écran se refermait dans la foulée de son ouverture et « Affichage > Écran de
 * veille » semblait ne rien faire du tout.
 */
export const WAKE_GRACE_MS = 700;

/** Pas de temps maximal : un onglet en arrière-plan revient avec plusieurs
 *  secondes d'un coup, et Steve traverserait l'écran en une image. */
const MAX_STEP_S = 0.05;

export interface Bounce {
  readonly x: number;
  readonly y: number;
  readonly vx: number;
  readonly vy: number;
}

export interface Bounds {
  readonly width: number;
  readonly height: number;
  /** Taille du poisson : il rebondit sur son bord, pas sur son centre. */
  readonly spriteWidth: number;
  readonly spriteHeight: number;
}

export interface StepResult {
  readonly next: Bounce;
  /** Vrai quand les deux composantes se sont inversées dans la même image. */
  readonly corner: boolean;
}

/**
 * Position et direction de départ.
 *
 * L'angle est tiré au hasard mais **jamais trop près d'un axe** : à 2° près de
 * l'horizontale, Steve met une minute à redescendre et l'écran de veille a l'air
 * cassé. On tire donc dans un quart de tour, puis on choisit les signes.
 */
export function startBounce(bounds: Bounds, random: () => number = Math.random): Bounce {
  const angle = (25 + random() * 40) * (Math.PI / 180);
  const signX = random() < 0.5 ? -1 : 1;
  const signY = random() < 0.5 ? -1 : 1;
  return {
    x: (bounds.width - bounds.spriteWidth) * (0.25 + random() * 0.5),
    y: (bounds.height - bounds.spriteHeight) * (0.25 + random() * 0.5),
    vx: Math.cos(angle) * SPEED_PX_S * signX,
    vy: Math.sin(angle) * SPEED_PX_S * signY,
  };
}

/** Avance d'un pas de temps, en rebondissant sur les bords. */
export function step(state: Bounce, bounds: Bounds, dt: number): StepResult {
  const seconds = Math.min(Math.max(0, dt), MAX_STEP_S);
  const maxX = Math.max(0, bounds.width - bounds.spriteWidth);
  const maxY = Math.max(0, bounds.height - bounds.spriteHeight);

  let { x, y, vx, vy } = state;
  x += vx * seconds;
  y += vy * seconds;

  let hitX = false;
  let hitY = false;

  if (x <= 0) {
    x = 0;
    vx = Math.abs(vx);
    hitX = true;
  } else if (x >= maxX) {
    x = maxX;
    vx = -Math.abs(vx);
    hitX = true;
  }

  if (y <= 0) {
    y = 0;
    vy = Math.abs(vy);
    hitY = true;
  } else if (y >= maxY) {
    y = maxY;
    vy = -Math.abs(vy);
    hitY = true;
  }

  return { next: { x, y, vx, vy }, corner: hitX && hitY };
}
